'use client';

// Legacy: pro-frontend/src/app/manage-portfolio/newpreview-portfolio/templates/
//   architect-template/architect-template.component.{ts,html,scss}
// Bespoke hero-based premium layout — NOT the shared sidebar shell used by
// classic/minimal.

import { useState } from 'react';

import './architect.css';

import type { PortfolioCategoryEntry } from '../../types';
import {
  formatEntryDate,
  getEntryField,
  getEntryImages,
  getEntryLinks,
  hasLanguageSkills,
  splitTitle,
  titleCaseName,
} from './entry-helpers';
import { nl2brSanitize } from './sanitize-html';
import { SocialLinksNav } from './social-links';
import type { PortfolioTemplateProps } from './types';
import { getYoutubeEmbedUrl } from './youtube-embed';

function entryTitle(entry: PortfolioCategoryEntry): string {
  return String(
    entry.display_title ||
      entry.title ||
      getEntryField(entry, 'education_title') ||
      'Untitled',
  );
}

/** Legacy `getPrimaryRoleTitle`: first non-hidden entry title in the first `work` category. */
function primaryRoleTitle(data: PortfolioTemplateProps['data']): string {
  const workCat = (data.categories || []).find(
    (c) => c.category_type === 'work' && c.entries && c.entries.length > 0,
  );
  if (!workCat || !workCat.entries) return '';
  const entry = workCat.entries.find((e) => !e.is_hidden) || workCat.entries[0];
  return entry ? String(entry.title || entry.display_title || '') : '';
}

function isSpecializationInTitle(entry: PortfolioCategoryEntry): boolean {
  const spec = getEntryField(entry, 'education_specialization_title');
  if (!spec) return false;
  const title = entryTitle(entry);
  return title.toLowerCase().includes(spec.toLowerCase());
}

function isDuplicateDescription(entry: PortfolioCategoryEntry): boolean {
  const desc = entry.description || entry.display_description || '';
  if (!desc) return false;
  const institution = getEntryField(entry, 'education_institution_name');
  if (institution && desc.includes(institution) && desc.includes(' | ')) {
    return true;
  }
  const spec = getEntryField(entry, 'education_specialization_title');
  if (spec && desc.trim().toLowerCase() === spec.trim().toLowerCase()) {
    return true;
  }
  return false;
}

export function ArchitectTemplate({
  data,
  isPublicView,
  theme,
  primaryColor,
  editHref,
}: PortfolioTemplateProps) {
  const [entrySelectedImage, setEntrySelectedImage] = useState<Record<string, string>>({});
  const [selectedPreviewImage, setSelectedPreviewImage] = useState<string | null>(null);

  const name = titleCaseName(data.name);
  const designation = data.designation;

  const roleTitle = primaryRoleTitle(data);
  const roleParts = roleTitle ? splitTitle(roleTitle) : { first: '', rest: '' };
  const heroPrimary = roleParts.first || data.company || '';
  const heroSecondary = roleParts.rest || data.role_highlight || '';

  const categories = (data.categories || []).filter(
    (c) => c.title && c.entries && c.entries.length > 0,
  );

  function entryKey(entry: PortfolioCategoryEntry, fallbackIndex: number): string {
    return String(entry.id || entryTitle(entry) || fallbackIndex);
  }

  function getSelectedImage(entry: PortfolioCategoryEntry, fallbackIndex: number): string {
    const key = entryKey(entry, fallbackIndex);
    if (entrySelectedImage[key]) return entrySelectedImage[key];
    const images = getEntryImages(entry);
    const first = images[0];
    return first ? String(first.file_url || '') : '';
  }

  function renderLinks(entry: PortfolioCategoryEntry) {
    const links = getEntryLinks(entry);
    if (links.length === 0) return null;
    return (
      <div className="resume-entry-links">
        {links.map((link, li) => {
          const embedUrl = getYoutubeEmbedUrl(link.link);
          if (embedUrl) {
            return (
              <div className="video-embed" key={String(link.id ?? li)}>
                <iframe src={embedUrl} allowFullScreen title="Embedded video" />
              </div>
            );
          }
          if (!link.link) return null;
          if (link.link_meta_title) {
            const hasImage = Boolean(
              link.link_meta_image_url &&
                link.link_meta_image_url !== 'null' &&
                link.link_meta_image_url !== 'undefined',
            );
            return (
              <div className="portfolio_in_site_link clearfix" key={String(link.id ?? li)}>
                {hasImage ? (
                  <div className="inner_portfolio_public">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={link.link_meta_image_url} alt="" />
                  </div>
                ) : null}
                <div className={`profolio_links_in_wrap${hasImage ? '' : ' no-image'}`}>
                  <h3 className="ellipsis">{link.link_meta_title}</h3>
                  <h4 className="ellipsis">
                    <a href={link.link} target="_blank" rel="noopener noreferrer">
                      {link.link}
                    </a>
                  </h4>
                  {link.link_meta_description ? (
                    <p className="ellipsis-multiline">{link.link_meta_description}</p>
                  ) : null}
                  <div className="profolio_links_in">
                    <a
                      href={link.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ backgroundColor: primaryColor }}
                    >
                      Visit Link
                    </a>
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div className="portfolio_in_site_link clearfix" key={String(link.id ?? li)}>
              <div className="profolio_links_in_wrap no-image">
                <h4 className="ellipsis">
                  <a href={link.link} target="_blank" rel="noopener noreferrer">
                    {link.link}
                  </a>
                </h4>
                <div className="profolio_links_in">
                  <a
                    href={link.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Visit Link
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={`template-architect-premium ${theme}`}
      style={{ ['--primary-color' as string]: primaryColor || '#ff6e00' }}
    >
      <div className="orange-box" style={{ backgroundColor: primaryColor }} />

      <header className="main-header">
        <div className="sidebar-orange">
          <div className="profile-card">
            {data.profile_image ? (
              <div className="profile-photo">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={data.profile_image} alt="Profile" />
              </div>
            ) : null}
            <h2>{name}</h2>
            {designation ? <p className="short-bio">{designation}</p> : null}
            {!isPublicView && editHref ? (
              <a href={editHref} className="architect-edit-link" title="Edit Portfolio">
                <i className="fa-solid fa-pen" aria-hidden="true" /> Edit
              </a>
            ) : null}
            <div className="p5_portfolio_public_menu">
              <SocialLinksNav data={data} />
            </div>
          </div>
        </div>

        <div className="hero-white">
          <div className="hero-content">
            <div className="hero-title">
              <h2>
                <span className="hero-title-primary">{heroPrimary}</span>
                <span className="hero-title-secondary">{heroSecondary}</span>
              </h2>
            </div>
            {data.summary ? <p className="hero-summary">{data.summary}</p> : null}
          </div>
        </div>
      </header>

      <div className="main-body">
        <main className="main-sections">
          {categories.map((cat) => {
            const title = splitTitle(cat.title);
            return (
              <section className="body-section" key={String(cat.id ?? cat.title)}>
                <div className="section-title">
                  <h2>
                    {title.first}
                    <br />
                    <span className="section-title-secondary">{title.rest}</span>
                  </h2>
                </div>

                <div className="projects-list">
                  {(cat.entries || []).map((entry, ei) => {
                    const images = getEntryImages(entry);
                    const selectedImage = getSelectedImage(entry, ei);
                    const institution =
                      getEntryField(entry, 'education_institution_name') || entry.work_company;
                    const workLocation = getEntryField(entry, 'work_location');
                    const dates = formatEntryDate(entry);
                    const specTitle = getEntryField(entry, 'education_specialization_title');
                    const score = getEntryField(entry, 'education_score');
                    const scoreType = getEntryField(entry, 'education_score_type');
                    const description = entry.description || entry.display_description || '';
                    const showSpecSeparate = specTitle && !isSpecializationInTitle(entry);
                    const showScoreAlone = (!specTitle || isSpecializationInTitle(entry)) && score;

                    return (
                      <div className="project-card" key={entryKey(entry, ei)}>
                        {images.length > 0 ? (
                          <div className="project-attachments">
                            <div className="image-row">
                              {selectedImage ? (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img
                                  src={selectedImage}
                                  className="main-preview-img"
                                  alt="Attachment preview"
                                  onClick={() => setSelectedPreviewImage(selectedImage)}
                                />
                              ) : null}
                              {images.length > 1 ? (
                                <div className="image-stack">
                                  {images.map((file, fi) => (
                                    // eslint-disable-next-line @next/next/no-img-element
                                    <img
                                      key={String(file.id ?? fi)}
                                      src={file.file_url}
                                      alt={file.file_title || 'Attachment'}
                                      className={selectedImage === file.file_url ? 'active' : undefined}
                                      onClick={() =>
                                        setEntrySelectedImage((prev) => ({
                                          ...prev,
                                          [entryKey(entry, ei)]: String(file.file_url || ''),
                                        }))
                                      }
                                    />
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ) : null}

                        <div className="project-content">
                          <h3>{entryTitle(entry)}</h3>
                          {institution ? (
                            <p className="education-institution">{institution}</p>
                          ) : null}
                          {workLocation ? (
                            <p className="education-location">
                              <i className="fa-solid fa-location-dot" aria-hidden="true" />
                              {workLocation}
                            </p>
                          ) : null}
                          {dates && cat.category_type !== 'skill' ? (
                            <p className="education-dates">{dates}</p>
                          ) : null}
                          {showSpecSeparate ? (
                            <p className="education-specialization">
                              {specTitle}
                              {score ? (
                                <span className="education-score">
                                  {' '}
                                  | {score} {scoreType}
                                </span>
                              ) : null}
                            </p>
                          ) : null}
                          {showScoreAlone ? (
                            <p className="education-score">
                              {score} {scoreType}
                            </p>
                          ) : null}
                          {description && !isDuplicateDescription(entry) ? (
                            <div
                              className="education-description"
                              dangerouslySetInnerHTML={{ __html: nl2brSanitize(description) }}
                            />
                          ) : null}
                          {cat.category_type === 'language' &&
                          hasLanguageSkills(entry) &&
                          cat.language_display_mode !== 'name_only' ? (
                            <div className="language-proficiency">
                              {getEntryField(entry, 'listening_level') ? (
                                <p className="proficiency-item">
                                  Listening: {getEntryField(entry, 'listening_level')}
                                </p>
                              ) : null}
                              {getEntryField(entry, 'reading_level') ? (
                                <p className="proficiency-item">
                                  Reading: {getEntryField(entry, 'reading_level')}
                                </p>
                              ) : null}
                              {getEntryField(entry, 'spoken_interaction_level') ? (
                                <p className="proficiency-item">
                                  Spoken Interaction: {getEntryField(entry, 'spoken_interaction_level')}
                                </p>
                              ) : null}
                              {getEntryField(entry, 'spoken_production_level') ? (
                                <p className="proficiency-item">
                                  Spoken Production: {getEntryField(entry, 'spoken_production_level')}
                                </p>
                              ) : null}
                              {getEntryField(entry, 'writing_level') ? (
                                <p className="proficiency-item">
                                  Writing: {getEntryField(entry, 'writing_level')}
                                </p>
                              ) : null}
                            </div>
                          ) : null}
                        </div>

                        {renderLinks(entry)}

                        {!isPublicView ? (
                          <span className="show-hide check_all_advance" title="Visible to public">
                            <i
                              className={entry.is_hidden ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'}
                              aria-label={entry.is_hidden ? 'Hidden' : 'Visible'}
                            />
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </main>
      </div>

      <footer>
        <p>
          Made with{' '}
          <a href="https://kudoswall.com" className="kudos-link" target="_blank" rel="noopener noreferrer">
            KudosWall
          </a>
        </p>
      </footer>

      {selectedPreviewImage ? (
        <div className="preview-overlay" onClick={() => setSelectedPreviewImage(null)}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={selectedPreviewImage} className="preview-img" alt="Enlarged view" />
            <div className="preview-close" onClick={() => setSelectedPreviewImage(null)}>
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
